import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
const defaultState = {
    name: '',
    serverAddress: '',
    endpointPath: '/',
    enabled: true,
    pollingIntervalSeconds: 300,
    responseTimeoutMs: 5000,
    resultType: 'http_code',
    expectedHttpCode: 200,
    bodyPattern: '',
    bodyScript: '',
    notificationEnabled: false,
    notificationType: 'email',
    notificationConfig: `{
  "recipients": ["alerts@example.com"]
}`
};
const ServiceForm = ({ service, onSave, onDelete }) => {
    const [state, setState] = useState(defaultState);
    const [error, setError] = useState(null);
    const [isSaving, setSaving] = useState(false);
    useEffect(() => {
        if (service) {
            setState({
                name: service.name,
                serverAddress: service.serverAddress,
                endpointPath: service.endpointPath || '/',
                enabled: service.enabled,
                pollingIntervalSeconds: service.pollingIntervalSeconds,
                responseTimeoutMs: service.responseTimeoutMs,
                resultType: service.resultType,
                expectedHttpCode: service.expectedHttpCode,
                bodyPattern: service.bodyPattern || '',
                bodyScript: service.bodyScript || '',
                notificationEnabled: service.notificationEnabled,
                notificationType: service.notificationType || 'email',
                notificationConfig: service.notificationConfig
                    ? JSON.stringify(service.notificationConfig, null, 2)
                    : '{}'
            });
        }
        else {
            setState({ ...defaultState });
        }
    }, [service]);
    const handleChange = (field, value) => {
        if (field === 'notificationType' && typeof value === 'string') {
            setState((prev) => {
                const trimmed = (prev.notificationConfig || '').trim();
                const isEmptyConfig = trimmed === '{}' || trimmed === '';
                let updatedConfig = prev.notificationConfig;
                if (isEmptyConfig) {
                    updatedConfig =
                        value === 'slack'
                            ? `{
  "webhookUrl": ""
}`
                            : `{
  "recipients": ["alerts@example.com"]
}`;
                }
                return { ...prev, notificationType: value, notificationConfig: updatedConfig };
            });
            return;
        }
        setState((prev) => ({ ...prev, [field]: value }));
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        let notificationConfig = null;
        if (state.notificationConfig.trim()) {
            try {
                notificationConfig = JSON.parse(state.notificationConfig);
            }
            catch (err) {
                setError('Notification config must be valid JSON');
                return;
            }
        }
        const payload = {
            ...state,
            serverAddress: state.serverAddress.trim().replace(/\/+$/, ''),
            endpointPath: state.endpointPath.startsWith('/')
                ? state.endpointPath
                : `/${state.endpointPath}`,
            notificationConfig
        };
        setSaving(true);
        try {
            await onSave(payload, !service);
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setSaving(false);
        }
    };
    const handleDelete = async () => {
        if (!service || !onDelete)
            return;
        if (window.confirm(`Delete service ${service.name}?`)) {
            await onDelete(service.id);
        }
    };
    return (_jsxs("form", { onSubmit: handleSubmit, className: "content-grid", style: { gap: '1rem' }, children: [_jsxs("div", { className: "form-row", children: [_jsxs("div", { children: [_jsx("label", { children: "Name" }), _jsx("input", { value: state.name, required: true, onChange: (event) => handleChange('name', event.target.value) })] }), _jsxs("div", { children: [_jsx("label", { children: "Server address" }), _jsx("input", { value: state.serverAddress, required: true, placeholder: "https://api.example.com", onChange: (event) => handleChange('serverAddress', event.target.value) })] }), _jsxs("div", { children: [_jsx("label", { children: "Endpoint path" }), _jsx("input", { value: state.endpointPath, required: true, placeholder: "/status", onChange: (event) => handleChange('endpointPath', event.target.value) })] })] }), _jsxs("div", { className: "form-row", children: [_jsxs("div", { children: [_jsx("label", { children: "Polling interval (seconds)" }), _jsx("input", { type: "number", min: 30, value: state.pollingIntervalSeconds, onChange: (event) => handleChange('pollingIntervalSeconds', Number(event.target.value)) })] }), _jsxs("div", { children: [_jsx("label", { children: "Response timeout (ms)" }), _jsx("input", { type: "number", min: 500, value: state.responseTimeoutMs, onChange: (event) => handleChange('responseTimeoutMs', Number(event.target.value)) })] })] }), _jsxs("div", { className: "form-row", children: [_jsxs("div", { children: [_jsx("label", { children: "Result type" }), _jsxs("select", { value: state.resultType, onChange: (event) => handleChange('resultType', event.target.value), children: [_jsx("option", { value: "http_code", children: "HTTP code" }), _jsx("option", { value: "body_regex", children: "Body regex" }), _jsx("option", { value: "body_script", children: "Body JS function" })] })] }), state.resultType === 'http_code' && (_jsxs("div", { children: [_jsx("label", { children: "Expected HTTP code" }), _jsx("input", { type: "number", value: state.expectedHttpCode, onChange: (event) => handleChange('expectedHttpCode', Number(event.target.value)) })] })), state.resultType === 'body_regex' && (_jsxs("div", { children: [_jsx("label", { children: "Regex pattern" }), _jsx("input", { value: state.bodyPattern, onChange: (event) => handleChange('bodyPattern', event.target.value) })] })), state.resultType === 'body_script' && (_jsxs("div", { children: [_jsx("label", { children: "JS function body" }), _jsx("textarea", { rows: 4, value: state.bodyScript, onChange: (event) => handleChange('bodyScript', event.target.value) })] }))] }), _jsxs("div", { className: "form-row", children: [_jsxs("div", { children: [_jsx("label", { children: "Service enabled" }), _jsxs("select", { value: state.enabled ? 'true' : 'false', onChange: (event) => handleChange('enabled', event.target.value === 'true'), children: [_jsx("option", { value: "true", children: "Enabled" }), _jsx("option", { value: "false", children: "Disabled" })] })] }), _jsxs("div", { children: [_jsx("label", { children: "Notifications" }), _jsxs("select", { value: state.notificationEnabled ? 'true' : 'false', onChange: (event) => handleChange('notificationEnabled', event.target.value === 'true'), children: [_jsx("option", { value: "false", children: "Disabled" }), _jsx("option", { value: "true", children: "Enabled" })] })] }), _jsxs("div", { children: [_jsx("label", { children: "Notification type" }), _jsxs("select", { value: state.notificationType, onChange: (event) => handleChange('notificationType', event.target.value), children: [_jsx("option", { value: "email", children: "Email" }), _jsx("option", { value: "slack", children: "Slack" })] })] })] }), _jsxs("div", { children: [_jsx("label", { children: "Notification config (JSON)" }), _jsxs("p", { style: {
                            margin: '0 0 0.35rem',
                            fontSize: '0.8rem',
                            color: 'rgba(255,255,255,0.7)'
                        }, children: ["Email example: ", _jsx("code", { children: '{ "recipients": ["alerts@example.com"] }' }), _jsx("br", {}), "Slack example:", ' ', _jsx("code", { children: '{ "webhookUrl": "https://hooks.slack.com/..." }' })] }), _jsx("textarea", { rows: 4, value: state.notificationConfig, onChange: (event) => handleChange('notificationConfig', event.target.value) })] }), error && _jsx("div", { style: { color: 'tomato' }, children: error }), _jsx("div", { style: { display: 'flex', justifyContent: 'space-between' }, children: _jsxs("div", { style: { display: 'flex', gap: '0.5rem' }, children: [_jsx("button", { type: "submit", disabled: isSaving, children: isSaving ? 'Saving...' : service ? 'Update service' : 'Create service' }), service && onDelete && (_jsx("button", { type: "button", style: { background: 'tomato', color: '#000' }, onClick: handleDelete, children: "Delete" }))] }) })] }));
};
export default ServiceForm;
